import { isValidPhoneNumber } from 'libphonenumber-js';
import React, { useId, useMemo, useState, useCallback } from 'react';

import { validateFiscalCode } from '@modules/logic/fiscalCode';

import Icon from '../icon/Icon';

import './input.css';

function validate(value, type, { region = 'IT', required = false } = {}) {
  const empty = value === '' || value == null;

  // Se non è required e il valore è vuoto, non validare
  if (!required && empty) return null;

  if (type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(String(value || ''))) return 'Email non valida';
  }

  if (type === 'tel') {
    // con defaultCountry/region possiamo accettare numeri nazionali
    if (!isValidPhoneNumber(String(value || ''), region)) {
      return 'Numero di telefono non valido';
    }
  }

  if (type === 'cf') {
    if (!validateFiscalCode(value).valid) return 'Codice fiscale non valido';
  }

  return null;
}

const Input = ({
  type = 'text',
  id,
  name,
  value,
  checked, // supporto retro per checkbox controllati
  placeholder,
  label, // se assente, usa placeholder come testo etichetta
  disabled = false,
  readonly = false,
  className = '',
  fullWidth = false, // Aggiunto supporto per fullWidth
  rows = 4, // default per textarea
  min,
  max,
  step,
  region = 'IT',
  required = false,
  change, // retro: handler personalizzato
  blur, // retro: handler personalizzato
  onChange, // compat standard React
  onBlur, // compat standard React
  inputId: _inputId, // react-select prop — ignored here to prevent DOM leakage
  error: externalError, // caller's validation (needs more than the value, e.g. CF vs name)
  ...rest
}) => {
  const autoId = useId();
  const inputId = id || autoId;
  const errorId = `${inputId}-error`;

  const [ownError, setError] = useState(null);
  const error = externalError || ownError;
  // A caller's description (field hint) is kept alongside the validation error.
  const describedBy =
    [error ? errorId : null, rest['aria-describedby']].filter(Boolean).join(' ') || undefined;

  const isCheckbox = type === 'checkbox';
  const isTextarea = type === 'textarea';
  const isCustomCF = type === 'cf';

  // tipo HTML effettivo da rendere
  const htmlType = isTextarea ? undefined : isCustomCF ? 'text' : type;

  const showLabel = Boolean(label || placeholder);
  const labelText = label || placeholder || '';

  // Retro: se 'checked' non è passato, per i checkbox usa 'value' (truthy/falsy)
  const currentValue = useMemo(() => {
    if (isCheckbox) return typeof checked !== 'undefined' ? !!checked : !!value;
    return value ?? '';
  }, [isCheckbox, checked, value]);

  const emitChange = useCallback(
    (e) => {
      change?.(e); // tua API
      onChange?.(e); // standard
    },
    [change, onChange]
  );

  const emitBlur = useCallback(
    (e) => {
      blur?.(e); // tua API (ora con evento)
      onBlur?.(e); // standard
    },
    [blur, onBlur]
  );

  const runValidation = useCallback(
    (val) => {
      const maybeError = validate(val, isCustomCF ? 'cf' : type, { region, required });
      setError(maybeError);
      return maybeError;
    },
    [isCustomCF, type, region, required]
  );

  const handleChange = useCallback(
    (e) => {
      const nextVal = isCheckbox ? !!e.target.checked : e.target.value;

      // valida in base al tipo "logico" (cf, tel, email...), con region/required
      runValidation(nextVal);

      // Propaga sempre l'evento (retro-compat)
      emitChange(e);
    },
    [emitChange, isCheckbox, runValidation]
  );

  // Rivalida anche quando si esce dal campo (formato corretto su blur).
  const handleBlur = useCallback(
    (e) => {
      const val = isCheckbox ? !!e.target.checked : e.target.value;
      runValidation(val);
      emitBlur(e);
    },
    [emitBlur, isCheckbox, runValidation]
  );

  // --- Checkbox ---
  if (isCheckbox) {
    return (
      <div className={`form-check ${className} ${fullWidth ? 'w-full' : ''}`.trim()}>
        <input
          {...rest}
          id={inputId}
          name={name}
          type="checkbox"
          className={`form-check-input ${fullWidth ? 'w-full' : ''}`.trim()}
          disabled={disabled}
          readOnly={readonly}
          onChange={handleChange}
          onBlur={emitBlur}
          checked={currentValue}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required}
        />
        {showLabel && (
          <label htmlFor={inputId} className="form-check-label">
            {labelText}
          </label>
        )}
        {error && (
          <div id={errorId} className="app-input__error">
            <Icon name="error" className="app-input__error-icon" />
            {error}
          </div>
        )}
      </div>
    );
  }

  // --- Textarea ---
  if (isTextarea) {
    return (
      <div className={`form-group ${className} ${fullWidth ? 'w-full' : ''}`.trim()}>
        <label
          htmlFor={inputId}
          className={`app-input__label ${labelText ? '' : 'app-input__label--hidden'}`.trim()}
        >
          {labelText}
        </label>
        <textarea
          {...rest}
          id={inputId}
          name={name}
          className={`form-control app-input ${error ? 'app-input--error' : ''} ${
            fullWidth ? 'w-full' : ''
          }`.trim()}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readonly}
          rows={rows}
          value={currentValue}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required}
        />
        {error && (
          <div id={errorId} className="app-input__error">
            <Icon name="error" className="app-input__error-icon" />
            {error}
          </div>
        )}
      </div>
    );
  }

  // --- Input standard ---
  return (
    <div className={`form-group ${className} ${fullWidth ? 'w-full' : ''}`.trim()}>
      <label
        htmlFor={inputId}
        className={`app-input__label ${labelText ? '' : 'app-input__label--hidden'}`.trim()}
      >
        {labelText}
      </label>
      <input
        {...rest}
        id={inputId}
        name={name}
        type={htmlType}
        className={`form-control app-input ${error ? 'app-input--error' : ''} ${
          fullWidth ? 'w-full' : ''
        }`.trim()}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        value={currentValue}
        min={min}
        max={max}
        step={step}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        required={required}
        inputMode={htmlType === 'number' ? 'decimal' : undefined}
      />
      {error && (
        <div id={errorId} className="app-input__error">
          <Icon name="error" className="app-input__error-icon" />
          {error}
        </div>
      )}
    </div>
  );
};

export default Input;
